// src/components/admin/EnhancedStaffManagement.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';
import { getStaff, saveStaff, deleteStaff } from '../../services/staffService';
import { FiPlus, FiEdit, FiTrash2, FiUser, FiMail, FiPhone, FiShield, FiSave, FiX, FiMoreVertical, FiCalendar, FiClock, FiEye } from 'react-icons/fi';
import RichTextEditor from '../common/RichTextEditor';
import AppointmentModal from '../calendar/AppointmentModal';
import StaffProfile from '../staff/StaffProfile';
import { db, functions } from '../../config/firebase';
import { collection, getDocs, doc, updateDoc, deleteDoc, addDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';
import ConfirmationModal from '../common/ConfirmationModal';
import { useConfirmation } from '../../hooks/useConfirmation';

const DEFAULT_NEW_STAFF = {
  firstName: '',
  lastName: '',
  email: '',
  role: 'staff',
  department: '',
  phone: '',
  dateOfBirth: '',
  address: {
    street: '',
    city: '',
    state: '',
    zipCode: ''
  },
  emergencyContact: {
    name: '',
    phone: ''
  },
  notes: '',
  availableForBooking: false
};

const EnhancedStaffManagement = ({ initialStaffId }) => {
  const { currentUser } = useAuth();
  const { currentTenant, hasPermission } = useTenant();
  const { confirmationState, showConfirmation, handleConfirm, handleCancel } = useConfirmation();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(DEFAULT_NEW_STAFF);
  const [error, setError] = useState('');
  
  // Action menu state
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [showBlockTimeModal, setShowBlockTimeModal] = useState(false);
  const [selectedStaffMember, setSelectedStaffMember] = useState(null);
  const [showStaffProfile, setShowStaffProfile] = useState(false);

  // Load staff from tenant-scoped Firestore collection
  const loadStaff = async () => {
    if (!currentTenant?.id) {
      setStaff([]);
      return;
    }

    if (!db) {
      console.error('Firestore is not available');
      toast.error('Firestore is not available. Cannot load staff members.');
      setStaff([]);
      return;
    }

    setLoading(true);
    try {
      // Load from tenant-scoped staff collection
      console.log('Loading staff from Firestore path:', `tenants/${currentTenant.id}/staff`);
      const querySnapshot = await getDocs(collection(db, 'tenants', currentTenant.id, 'staff'));
      const staffList = [];
      querySnapshot.forEach((doc) => {
        const staffData = doc.data();
        console.log('Found staff member:', { id: doc.id, ...staffData });
        staffList.push({
          id: doc.id,
          ...staffData
        });
      });
      console.log('Total staff members loaded:', staffList.length);
      setStaff(staffList);
    } catch (error) {
      console.error('Error loading staff:', error);
      toast.error('Error loading staff members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStaff();
  }, [currentTenant]);

  // Auto-open staff profile if initialStaffId is provided
  useEffect(() => {
    if (initialStaffId && staff.length > 0) {
      const staffMember = staff.find(s => s.id === initialStaffId);
      if (staffMember) {
        setSelectedStaffMember(staffMember);
        setShowStaffProfile(true);
      }
    }
  }, [initialStaffId, staff]);

  const handleAddStaff = async () => {
    if (!form.firstName || !form.lastName || !form.email) {
      setError('Please fill in all required fields');
      return;
    }

    if (!currentTenant?.id) {
      setError('No clinic selected. Please select a clinic first.');
      return;
    }

    setLoading(true);
    try {
      // Use Cloud Function to create staff account with email verification
      const createStaffAccount = httpsCallable(functions, 'createStaffAccount');
      
      const result = await createStaffAccount({
        userData: {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          role: form.role || 'staff',
          phone: form.phone || '',
          dateOfBirth: form.dateOfBirth || '',
          address: form.address || {
            street: '',
            city: '',
            state: '',
            zipCode: ''
          },
          emergencyContact: form.emergencyContact || {
            name: '',
            phone: ''
          },
          notes: form.notes || '',
          department: form.department || ''
        },
        tenantId: currentTenant.id
      });
      
      console.log('Staff account created successfully:', result.data);
      
      await loadStaff();
      setShowAddForm(false);
      setForm(DEFAULT_NEW_STAFF);
      setError('');
      toast.success(`Staff member ${form.firstName} ${form.lastName} created successfully! Welcome email sent.`);
    } catch (error) {
      console.error('Error creating staff account:', error);
      setError(error.message || 'Error creating staff account');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStaff = async () => {
    if (!editingId) return;

    if (!currentTenant?.id) {
      toast.error('No clinic selected. Cannot update staff member.');
      return;
    }

    if (!db) {
      toast.error('Firestore is not available. Cannot update staff member.');
      return;
    }

    setLoading(true);
    try {
      await updateDoc(doc(db, 'tenants', currentTenant.id, 'staff', editingId), {
        ...form,
        updatedAt: new Date().toISOString()
      });

      await loadStaff();
      setEditingId(null);
      setForm(DEFAULT_NEW_STAFF);
      setShowAddForm(false); // Close the dialog after update
      toast.success('Staff member updated successfully');
    } catch (error) {
      console.error('Error updating staff:', error);
      toast.error('Error updating staff member');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStaff = async (staffId) => {
    const confirmed = await showConfirmation({
      title: 'Delete Staff Member',
      message: 'Are you sure you want to delete this staff member? This cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    });
    
    if (!confirmed) return;

    if (!currentTenant?.id) {
      toast.error('No clinic selected. Cannot delete staff member.');
      return;
    }

    if (!db) {
      toast.error('Firestore is not available. Cannot delete staff member.');
      return;
    }

    setLoading(true);
    try {
      await deleteDoc(doc(db, 'tenants', currentTenant.id, 'staff', staffId));
      await loadStaff();
      toast.success('Staff member deleted successfully');
    } catch (error) {
      console.error('Error deleting staff:', error);
      toast.error('Error deleting staff member');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (staffMember) => {
    setForm(staffMember);
    setEditingId(staffMember.id);
    setShowAddForm(true);
    setError('');
  };

  const handleCancelEdit = () => {
    setShowAddForm(false);
    setEditingId(null);
    setForm(DEFAULT_NEW_STAFF);
    setError('');
  };

  // Action menu handlers
  const handleActionMenuToggle = (staffId) => {
    setActionMenuOpen(actionMenuOpen === staffId ? null : staffId);
  };

  const handleBlockTime = (staffMember) => {
    setSelectedStaffMember(staffMember);
    setShowBlockTimeModal(true);
    setActionMenuOpen(null);
  };

  const handleViewProfile = (staffMember) => {
    setSelectedStaffMember(staffMember);
    setShowStaffProfile(true);
    setActionMenuOpen(null);
  };

  const handleBlockTimeModalClose = () => {
    setShowBlockTimeModal(false);
    setSelectedStaffMember(null);
  };

  const handleStaffProfileClose = () => {
    setShowStaffProfile(false);
    setSelectedStaffMember(null);
  };

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'senior_clinician': return 'bg-purple-100 text-purple-800';
      case 'clinician': return 'bg-indigo-100 text-indigo-800';
      case 'junior_clinician': return 'bg-blue-100 text-blue-800';
      case 'staff': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!hasPermission('admin')) {
    return (
      <div className="text-center py-12">
        <FiShield className="mx-auto w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600">You don't have permission to manage staff members.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Staff Management</h2>
          <p className="text-gray-600">
            {currentTenant ? `Managing staff for: ${currentTenant.name}` : 'No clinic selected'}
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <FiPlus className="w-4 h-4" />
          <span>Add Staff Member</span>
        </button>
      </div>

      {/* Staff List */}
      <div className="bg-white rounded-xl border border-gray-200" style={{ overflow: 'visible' }}>
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading staff members...</p>
          </div>
        ) : staff.length === 0 ? (
          <div className="p-8 text-center">
            <FiUser className="mx-auto w-12 h-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Staff Members</h3>
            <p className="text-gray-600">Add your first staff member to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto" style={{ overflow: 'visible' }}>
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-6 font-medium text-gray-900">Name</th>
                  <th className="text-left py-3 px-6 font-medium text-gray-900">Email</th>
                  <th className="text-left py-3 px-6 font-medium text-gray-900">Role</th>
                  <th className="text-left py-3 px-6 font-medium text-gray-900">Department</th>
                  <th className="text-left py-3 px-6 font-medium text-gray-900">Phone</th>
                  <th className="text-left py-3 px-6 font-medium text-gray-900">Status</th>
                  <th className="text-left py-3 px-6 font-medium text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {staff.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <FiUser className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {member.firstName} {member.lastName}
                          </p>
                          <p className="text-sm text-gray-500">
                            {member.name && `(${member.name})`}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2">
                        <FiMail className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-900">{member.email}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getRoleColor(member.role)}`}>
                        {member.role?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-gray-900">
                      {member.department || '-'}
                    </td>
                    <td className="py-4 px-6">
                      {member.phone ? (
                        <div className="flex items-center space-x-2">
                          <FiPhone className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-900">{member.phone}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${member.isActive !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {member.isActive !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditClick(member)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <FiEdit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteStaff(member.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                        
                        {/* Action Menu */}
                        <div className="relative">
                          <button
                            onClick={() => handleActionMenuToggle(member.id)}
                            className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                            title="More actions"
                          >
                            <FiMoreVertical className="w-4 h-4" />
                          </button>
                          
                          {actionMenuOpen === member.id && (
                            <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1"
                                 style={{ 
                                   position: 'absolute',
                                   right: '0',
                                   top: '100%',
                                   marginTop: '4px',
                                   zIndex: 9999
                                 }}>
                              <button
                                onClick={() => handleViewProfile(member)}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                              >
                                <FiEye className="w-4 h-4" />
                                <span>View Profile</span>
                              </button>
                              <button
                                onClick={() => handleBlockTime(member)}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                              >
                                <FiCalendar className="w-4 h-4" />
                                <span>Block Time</span>
                              </button>
                              <button
                                onClick={() => {
                                  // TODO: Implement leave request functionality
                                  toast.info('Leave request functionality coming soon');
                                  setActionMenuOpen(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                              >
                                <FiClock className="w-4 h-4" />
                                <span>Request Leave</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingId ? 'Edit Staff Member' : 'Add New Staff Member'}
                </h3>
                <button
                  onClick={handleCancelEdit}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FiX className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700">{error}</p>
                </div>
              )}

              {/* Personal Information */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">Personal Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={form.firstName}
                      onChange={(e) => setForm(prev => ({ ...prev, firstName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter first name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={form.lastName}
                      onChange={(e) => setForm(prev => ({ ...prev, lastName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter last name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter email address"
                      disabled={editingId} // Don't allow email changes for existing users
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter phone number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      value={form.dateOfBirth}
                      onChange={(e) => setForm(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Work Information */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">Work Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Role
                    </label>
                    <select
                      value={form.role}
                      onChange={(e) => setForm(prev => ({ ...prev, role: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="staff">Staff</option>
                      <option value="junior_clinician">Junior Clinician</option>
                      <option value="clinician">Clinician</option>
                      <option value="senior_clinician">Senior Clinician</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Department
                    </label>
                    <input
                      type="text"
                      value={form.department}
                      onChange={(e) => setForm(prev => ({ ...prev, department: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter department"
                    />
                  </div>
                </div>
                
                {/* Online Booking Availability */}
                <div className="mt-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="availableForBooking"
                      checked={form.availableForBooking || false}
                      onChange={(e) => setForm(prev => ({ ...prev, availableForBooking: e.target.checked }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="availableForBooking" className="ml-3 text-sm font-medium text-gray-700">
                      Available for Online Booking
                    </label>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    When enabled, this staff member will appear in the public booking system for clients to select
                  </p>
                </div>
              </div>

              {/* Address Information */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">Address Information</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Street Address
                    </label>
                    <input
                      type="text"
                      value={form.address?.street || ''}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        address: { ...prev.address, street: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter street address"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        City
                      </label>
                      <input
                        type="text"
                        value={form.address?.city || ''}
                        onChange={(e) => setForm(prev => ({
                          ...prev,
                          address: { ...prev.address, city: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter city"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        State/Province
                      </label>
                      <input
                        type="text"
                        value={form.address?.state || ''}
                        onChange={(e) => setForm(prev => ({
                          ...prev,
                          address: { ...prev.address, state: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter state/province"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        ZIP/Postal Code
                      </label>
                      <input
                        type="text"
                        value={form.address?.zipCode || ''}
                        onChange={(e) => setForm(prev => ({
                          ...prev,
                          address: { ...prev.address, zipCode: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter ZIP/postal code"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">Emergency Contact</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Name
                    </label>
                    <input
                      type="text"
                      value={form.emergencyContact?.name || ''}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        emergencyContact: { ...prev.emergencyContact, name: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter emergency contact name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      value={form.emergencyContact?.phone || ''}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        emergencyContact: { ...prev.emergencyContact, phone: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter emergency contact phone"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">Additional Notes</h4>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                  placeholder="Enter any additional notes..."
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={editingId ? handleUpdateStaff : handleAddStaff}
                disabled={loading}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <FiSave className="w-4 h-4" />
                <span>{loading ? 'Saving...' : editingId ? 'Update Staff' : 'Add Staff'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Block Time Modal */}
      {showBlockTimeModal && selectedStaffMember && (
        <AppointmentModal
          mode="event"
          prefilledStaffId={selectedStaffMember.id}
          onClose={handleBlockTimeModalClose}
          onSaved={() => {
            handleBlockTimeModalClose();
            toast.success('Time blocked successfully');
          }}
          hours={[]} // Will use default hours
        />
      )}

      {/* Staff Profile Modal */}
      {showStaffProfile && selectedStaffMember && (
        <StaffProfile
          staffMember={selectedStaffMember}
          onClose={handleStaffProfileClose}
          onUpdate={loadStaff}
          initialTab={initialStaffId ? 'leave' : 'overview'}
        />
      )}

      {/* Click outside to close action menu */}
      {actionMenuOpen && (
        <div 
          className="fixed inset-0 z-5" 
          onClick={() => setActionMenuOpen(null)}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        type={confirmationState.type}
      />
    </div>
  );
};

export default EnhancedStaffManagement;
